<template>
    <v-flex d-block xs12>
        <v-card>
            <v-card-text>
                <v-layout>
                    <v-flex xs12 md-6 class="mt-4"><h4>{{ $t('cases.filters.title') }}</h4></v-flex>
                </v-layout>
                <v-layout>
                    <v-flex xs12 md3>
                        <v-select :items="availableCaseStatuses"
                                v-model="caseFiltersStatuses"
                                :label="$t('cases.filters.statuses.label')"
                                multiple
                                item-value="value"
                                item-text="title"
                                small-chips
                        ></v-select>
                    </v-flex>
                    <v-flex xs12 md3 class="ml-3">
                        <v-autocomplete
                                :items="filterServices"
                                v-model="caseFiltersServices"
                                item-text="titleFull"
                                item-value="id"
                                :no-data-text="$t('cases.filters.services.no-data.label')"
                                :label="$t('cases.filters.services.label')"
                                multiple
                                deletable-chips
                                small-chips
                                clearable
                        >
                            <template
                                    slot="selection"
                                    slot-scope="data"
                            >
                                <v-chip
                                    close
                                    class="chip--select-multi v-chip--small"
                                    @input="removeServiceFromFilter(data.item)"
                                >
                                    <v-tooltip bottom>
                                      <span slot="activator">
                                          <span v-if="data.item.externalId !== null">{{ data.item.code }} <span class="client-code">({{ data.item.externalId }})</span></span>
                                          <span v-else>{{ data.item.code }}</span>
                                      </span>
                                        {{ data.item.title }}
                                    </v-tooltip>
                                </v-chip>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                    <v-flex xs12 md3 class="ml-3">
                        <v-autocomplete
                                :items="filterSpecialists"
                                v-model="caseFiltersSpecialists"
                                item-text="name"
                                item-value="id"
                                :no-data-text="$t('cases.filters.specialists.no-data.label')"
                                :label="$t('cases.filters.specialists.label')"
                                multiple
                                deletable-chips
                                small-chips
                                clearable
                        >
                            <template
                                    slot="selection"
                                    slot-scope="data"
                            >
                                <v-chip
                                        close
                                        class="chip--select-multi v-chip--small"
                                        @input="removeSpecialistFromFilter(data.item)"
                                >
                                    <v-tooltip bottom>
                                      <span slot="activator">
                                        {{ data.item.initials }}
                                      </span>
                                        {{ data.item.name }}
                                    </v-tooltip>
                                </v-chip>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>
                <v-layout>
                    <v-flex xs12 md3>
                        <v-text-field
                                v-model="caseFiltersFulltextSearchString"
                                :label="$t('cases.filters.fulltextSearch.label')"
                                flat
                        ></v-text-field>
                    </v-flex>
                    <v-flex xs12 md6 class="ml-3">
                        {{$t('cases.filters.creationDatetime.label')}}:
                        <v-menu full-width offset-y :close-on-content-click="false" v-model="filterCaseDateRangeMenuVisible" bottom>
                            <v-btn color="primary" outline slot="activator">{{ cases.filters.createdFrom }} &mdash; {{ cases.filters.createdTo }}</v-btn>
                            <v-card>
                                <v-card-text>
                                    <v-daterange no-direct-enter
                                                 :options="cases.creationDateRangeFilterOptions"
                                                 :labels="{
                                                    start: $t('cases.filters.creationDatetime.start.label'),
                                                    end: $t('cases.filters.creationDatetime.end.label')
                                                 }"
                                                 @input="handleCaseFilterUpdateCreationDatetime"
                                                 no-presets/>
                                </v-card-text>
                            </v-card>
                        </v-menu>
                    </v-flex>
                </v-layout>
                <v-layout v-bind:style="{'display': 'inline-block', 'width': '100%'}">
                    <v-data-table
                            :headers="headers"
                            :items="cases.items"
                            :pagination.sync="cases.pagination"
                            :total-items="cases.totalItems"
                            :loading="cases.loading"
                            :rows-per-page-items="[10, 25, 40]"
                            :no-data-text="$t('cases.table.noResultText')"
                            :rows-per-page-text="$t('cases.table.itemsPerPage')"
                            class="elevation-1 cases-wide"
                    >
                        <template slot="headerCell" slot-scope="props">
                            <v-tooltip bottom v-if="props.header.shortText">
                              <span slot="activator">
                                {{ props.header.shortText }}
                              </span>
                              {{ props.header.text }}
                            </v-tooltip>
                            <span v-else>{{ props.header.text }}</span>
                        </template>
                        <template slot="items" slot-scope="props">
                            <tr @click="openViewCaseWindow(props.item)" :style="{'cursor': 'pointer'}">
                                <td v-if="!props.item.dpId">{{ props.item.title | NoEmpty}}</td>
                                <td v-else><a :href="fetchDPUrl + 'cases/' + props.item.dpId" target="_blank" @click.stop=''>{{ props.item.title}}</a></td>
                                <td>{{ props.item.creationDatetime | DateWithMinutes | NoEmpty}}</td>
                                <td>{{ props.item.receiptMaterialsDatetime | DateWithMinutes | NoEmpty}}</td>
                                <td v-if="props.item.status === 'validated' || props.item.status === 'archived'">
                                    <span v-if="props.item.reportValidationEndedDate">{{ props.item.reportValidationEndedDate | DateWithMinutes | NoEmpty}}</span><span v-else>-</span>
                                </td>
                                <td v-else-if="props.item.estimatedCompletionDatetime && props.item.lisId != ''">
                                    <div v-if="!props.item.onHold">
                                        <countdown :date=props.item.estimatedCompletionDatetime></countdown>
                                    </div>
                                    <div v-else>
                                        {{ secondsToHoursAndMinutes(props.item.secondsBeforeEstimatedCompletion) }}
                                    </div>
                                </td>
                                <td v-else>-</td>
                                <td v-if="props.item.onHold" class="hold-status-text">On hold</td>
                                <td v-else>{{ getFormattedStatus(props.item.status) | NoEmpty }}</td>
                                <td><service-code-badges :servicesById="servicesByIdForBadges" :caseServices="props.item.services"/></td>
                                <td v-if="props.item.status === 'validated' || props.item.status === 'archived'&& props.item.reportValidationEndedDate">
                                    <span v-if="props.item.reportSentAt" class="report-sent-label"><i class="fas fa-envelope-square"></i> {{ formatReportSentAt(props.item.reportSentAt) }}
                                    </span><br/><span class="show-report" @click.stop="handleShowReportClick(props.item)">{{ $t('cases.showReport') }}</span></td>
                                <td v-else>-</td>
                                <td>{{ props.item.patientName | NoEmpty}}</td>
                                <td>{{ props.item.externalLabel | NoEmpty}}</td>
                                <td>{{ getClientSpecialistName(props.item) }}</td>
                                <!--<td>{{ props.item.price | currency}}</td>-->
                            </tr>
                        </template>
                    </v-data-table>
                </v-layout>
                <v-layout row justify-center v-if="reportedCase">
                    <v-dialog v-model="reportedCase" persistent lazy max-width="1000px" min-height="1100px" no-click-animation>
                        <v-card>
                            <v-toolbar dark color="primary">
                                <v-btn icon dark @click="handleCloseCaseReport">
                                    <v-icon>close</v-icon>
                                </v-btn>
                                <v-toolbar-title>{{ $t('cases.showReport.case.title') }} {{ reportedCase.title }}</v-toolbar-title>
                                <v-spacer></v-spacer>
                            </v-toolbar>
                            <v-card-text>
                                <div>
                                    <div v-if="reportedCase.reportSentAt !== null">{{ $t('cases.showReport.sentAt.title') }} {{ reportedCase.reportSentAt | DateWithMinutes }}</div>
                                    <v-btn @click="handleClickReportDownloadButton(reportedCase, false)">{{ $t('cases.showReport.downloadButton.title') }} <v-icon class="fas fa-file-download" right dark></v-icon></v-btn>
                                    <v-btn v-if="currentClient.noHeader" @click="handleClickReportDownloadButton(reportedCase, true)">{{ $t('cases.showReport.downloadButton.titleNoHeader') }} <v-icon class="fas fa-file-download" right dark></v-icon></v-btn>
                                </div>
                                <div class="report-wrapper text-xs-center">
                                    <v-progress-circular
                                            :size="70"
                                            :width="7"
                                            style="margin-top: 230px"
                                            color="primary"
                                            indeterminate
                                            v-if="reportLoading"
                                    ></v-progress-circular>
                                    <pdf v-show="!reportLoading" :src="reportSource" @loaded="handleReportLoaded" :page="currentReportPage" @num-pages="currentReportPagesCount = $event"></pdf>
                                    <div v-if="!reportLoading">
                                        <v-pagination
                                                v-model="currentReportPage"
                                                :length="currentReportPagesCount"
                                                prev-icon="fas fa-caret-left"
                                                next-icon="fas fa-caret-right"
                                        ></v-pagination>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                </v-layout>
                <v-layout row justify-center v-if="viewedCase !== null">
                    <v-dialog v-model="viewCaseVisible" persistent lazy  no-click-animation width="1000">
                        <case-details :viewed-case="viewedCase"
                                      :specialists="currentClient.specialists"
                                      @close="closeViewCaseWindow"></case-details>
                    </v-dialog>
                </v-layout>
            </v-card-text>
        </v-card>
    </v-flex>
</template>

<script>

const { getBaseClientAccountApiURL } = require('../../helpers/baseurl')
import { mapActions, mapState } from 'vuex'
import NoEmpty from '../Filters/NoEmpty.js'
import DateWithMinutes from '../Filters/DateWithMinutes.js'
import Countdown from '../Countdown'
import ServiceCodeBadges from '../ServiceCodeBadges'
import SpecialistsInitials from '../SpecialistsInitials'
import CaseDetails from './CaseDetails'
import pdf from 'vue-pdf'
import api from '@/api'
import FileSaver from 'file-saver'

import moment from 'moment'
import _ from 'lodash'

export default {
    name: 'cases',
    components: {
      Countdown,
      ServiceCodeBadges,
      SpecialistsInitials,
      CaseDetails,
      pdf
    },
    filters: {
      NoEmpty,
      DateWithMinutes
    },
    data() {
      return {
        filterCaseDateRangeMenuVisible: false,
        caseDetailsVisible: false,
        viewedCase: null,
        reportedCase: null,
        reportLoading: false,
        reportSource: null,
        currentReportPage: 1,
        currentReportPagesCount: 0,
        headers: [
          {
            text: this.$t('cases.table.columns.header.title'),
            align: 'left',
            sortable: false,
            value: 'name',
            width: '200px'
          },
          {
            text: this.$t('cases.table.columns.header.creationDatetime'),
            value: 'creationDatetime',
            width: '150px',
            sortable: false
          },
          {
            text: this.$t('cases.table.columns.header.receiptMaterialsDatetime'),
            value: 'receiptMaterialsDatetime',
            width: '193px',
            sortable: false
          },
          {
            text: this.$t('cases.table.columns.header.reportValidatedAt'),
            value: 'reportValidatedAt',
            width: '186px',
            sortable: false
          },
          {
            text: this.$t('cases.table.columns.header.status'),
            value: 'status',
            width: '140px',
            sortable: false
          },
          {
            text: this.$t('cases.table.columns.header.services'),
            value: 'application.js',
            width: '200px',
            sortable: false
          },
          {
            text: this.$t('cases.table.columns.header.report'),
            value: 'report',
            width: '150px',
            sortable: false
          },
          {
            text: this.$t('cases.table.columns.header.patientName'),
            value: 'patientName',
            width: '280px',
            sortable: false
          },
          {
            text: this.$t('cases.table.columns.header.externalLabel'),
            value: 'externalLabel',
            width: '150px',
            sortable: false
          },
          {
            text: this.$t('cases.table.columns.header.clientSpecialist'),
            value: 'clientSpecialist',
            width: '250px',
            sortable: false
          }/*,
          {
            text: this.$t('cases.table.columns.header.price'),
            value: 'price',
            width: '150px',
            sortable: false
          }*/
        ]
      }
    },
    created () {
      this.loadCases()
    },
    watch: {
      pagination: {
        async handler () {
          this.loadCases()
        },
        deep: true
      }
    },
    computed: {
      ...mapState({
        availableCaseStatuses: 'availableCaseStatuses',
        caseDateRangeFilterOptions: 'caseDateRangeFilterOptions',
        cases: 'cases',
        availableCaseStatusesByValue: 'availableCaseStatusesByValue',
        availableServicesById: 'availableServicesById',
        currentClient: 'currentClient',
        servicesByClients: 'servicesByClients',
        pagination: state => state.cases.pagination,
        fetchDPUrl: state => state.dpURL
      }),
      caseFiltersStatuses: {
        get () {
          return this.cases.filters.statuses
        },
        set (values) {
          this.changeCasesFilterStatuses(values)
        }
      },
      caseFiltersServices: {
        get () {
          return this.cases.filters.serviceIds
        },
        set (values) {
          this.changeCasesFilterServices(values)
        }
      },
      caseFiltersSpecialists: {
        get () {
          return this.cases.filters.specialistIds
        },
        set (values) {
          this.changeCasesFilterSpecialists(values)
        }
      },
      caseFiltersFulltextSearchString: {
        get () {
          return this.cases.filters.fulltextSearchString
        },
        set: _.debounce(function (value) {
          let resultValue = null
          if (value.length > 0) {
            resultValue = value
          }

          this.changeCasesFilterFulltextSearch(resultValue)
        }, 500),
      },
      servicesByCurrentClient () {
        const currentClientId = _.get(this.currentClient, 'id', null);

        if (!currentClientId) {
          return []
        }

        return _.get(this.servicesByClients, currentClientId, {})
      },
      servicesByIdForBadges () {
        let result = {}
        for (let serviceId in this.servicesByCurrentClient) {
          if (!this.servicesByCurrentClient.hasOwnProperty(serviceId)) {
            continue
          }

          const clientService = this.servicesByCurrentClient[serviceId]
          const availableService = _.get(this.availableServicesById, serviceId, null)

          if (!availableService) {
            continue
          }

          result[serviceId] = {
            id: serviceId,
            code: availableService.code,
            title: availableService.title,
            externalId: clientService.externalId
          }
        }

        return result
      },
      filterServices () {
        let result = []

        for (let serviceId in this.servicesByCurrentClient) {
          if (!this.servicesByCurrentClient.hasOwnProperty(serviceId)) {
            continue
          }

          const clientService = this.servicesByCurrentClient[serviceId]
          const availableService = _.get(this.availableServicesById, serviceId, null)

          if (!availableService) {
            continue
          }

          let title = `${availableService.code} - ${availableService.title}`

          if (clientService.externalId !== null) {
            title = `${availableService.code} (${clientService.externalId}) - ${availableService.title}`
          }

          result.push({
            id: serviceId,
            code: availableService.code,
            titleFull: title,
            title: availableService.title,
            externalId: clientService.externalId
          })
        }

        return _.orderBy(result, 'code', 'asc')
      },
      filterSpecialists () {
        let parenthesesContentRegexp = /\([^)]*\)/g

        const specialists = _.get(this.currentClient, ['specialists'], [])

        let result = []
        for (let specialist of specialists) {
          let words = _.words(_.trim(_.replace(specialist.name, parenthesesContentRegexp, '')))

          let initials = ''
          for (let word of words) {
            initials += _.toUpper(word[0])
          }

          result.push({
            id: specialist.id,
            name: specialist.name,
            initials: initials
          })
        }

        return result
      }
    },
    methods: {
      ...mapActions({
        changeCasesFilterStatuses: 'changeCasesFilterStatuses',
        changeCasesFilterServices: 'changeCasesFilterServices',
        changeCasesFilterSpecialists: 'changeCasesFilterSpecialists',
        changeCasesFilterFulltextSearch: 'changeCasesFilterFulltextSearch',
        changeCasesFilterDates: 'changeCasesFilterDates',
        loadCases: 'loadCases'
      }),
      openViewCaseWindow (viewedCase) {
        this.viewedCase = viewedCase
        this.viewCaseVisible = true
      },
      closeViewCaseWindow () {
        this.viewedCase = null
        this.viewCaseVisible = false
      },
      handleReportLoaded () {
        this.reportLoading = false
      },
      handleShowReportClick (c) {
        this.reportedCase = c
        this.reportLoading = true

        const baseURL = getBaseClientAccountApiURL()

        this.reportSource = {
          url: `${baseURL}cases/get-report?caseId=${c.id}`
        }
      },
      handleCloseCaseReport () {
        this.reportLoading = false
        this.reportedCase = null
        this.reportSource = null
        this.currentReportPagesCount = 0
        this.currentReportPage = 1
      },
      secondsToHoursAndMinutes (seconds) {
        if (!seconds) {
          return '-'
        }

        let duration = moment.duration(seconds, 'seconds')
        return duration.format('HH:mm')
      },
      getFormattedStatus (statusValue) {
        return _.get(this.availableCaseStatusesByValue, [statusValue, 'title'], null)
      },
      formatReportSentAt (reportSentAt) {
        return moment(reportSentAt).format('YYYY-MM-DD')
      },
      getClientSpecialistName (c) {
        let specialistId = c.clientSpecialistId

        let specialists = _.get(this.currentClient, ['specialists'], null)

        if (specialists === null) {
          return '-'
        }

        let specialistIndex = _.findIndex(specialists, function (item) {
          return item.id === specialistId
        })

        if (specialistIndex < 0) {
          return '-'
        }

        return _.get(specialists, [specialistIndex, 'name'], '-')
      },
      async handleClickReportDownloadButton (c, noHeader) {
        try {
          let reportData = await api.getCaseReport(c.id, noHeader)
          let blob = new Blob([reportData], { type: 'application/pdf' })

          FileSaver.saveAs(blob, `${c.title}-report.pdf`)
        } catch (e) {
          console.log('Unable to download report')
        }
      },
      handleCaseFilterUpdateCreationDatetime (range) {
        this.changeCasesFilterDates({
          createdFrom: range[0],
          createdTo: range[1]
        })
      },
      removeServiceFromFilter (item) {
        const index = this.cases.filters.serviceIds.indexOf(item.id)
        if (index >= 0) {
          let services = _.cloneDeep(this.cases.filters.serviceIds)
          services.splice(index, 1)
          this.changeCasesFilterServices(services)
        }
      },
      removeSpecialistFromFilter (item) {
        const index = this.cases.filters.specialistIds.indexOf(item.id)
        if (index >= 0) {
          let specialists = _.cloneDeep(this.cases.filters.specialistIds)
          specialists.splice(index, 1)
          this.changeCasesFilterSpecialists(specialists)
        }
      }
    }
}
</script>

<style>
    .show-report {
        text-decoration: underline;
        cursor: pointer;
    }

    .show-report:hover {
        color: #1976d2;
    }

    .report-wrapper {
        min-height: 500px;
        height: 500px;
        overflow-y: scroll;
    }

    .report-sent-label {
        color: gray;
        font-size: 11px;
    }

    .hold-status-text {
        color: #ffac26;
    }

    .client-code {
        font-size: 10px;
    }
</style>
